import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  SelectChangeEvent,
  CircularProgress,
  ListSubheader,
  TextField,
  InputAdornment,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Button,
  Typography,
} from '@mui/material';
import {
  Store as StoreIcon,
  LocationOn as LocationIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { hybridApi } from '../services/api';

interface Store {
  id: string;
  name: string;
  city: string;
  state: string;
  brand: string;
  active: number;
  address1?: string;
}

interface EnhancedLocationSelectorProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  label?: string;
  showSearch?: boolean;
  showBrandFilter?: boolean;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 8 + ITEM_PADDING_TOP,
      width: 400,
    },
  },
};

const EnhancedLocationSelector: React.FC<EnhancedLocationSelectorProps> = ({ 
  value, 
  onChange, 
  multiple = false,
  label = 'Select Stores',
  showSearch = true,
  showBrandFilter = true
}) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState<string>('all');

  // Fetch all stores on mount
  useEffect(() => {
    const fetchStores = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await hybridApi.getStores();
        // API returns array directly
        const allStores = Array.isArray(response.data) ? response.data : [];
        // Sort stores by brand and then by ID
        const sortedStores = allStores.sort((a: Store, b: Store) => {
          if (a.brand !== b.brand) {
            return a.brand.localeCompare(b.brand);
          }
          return a.id.localeCompare(b.id);
        });
        setStores(sortedStores);
      } catch (err) {
        console.error('Error fetching stores:', err);
        setError('Failed to load stores');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStores();
  }, []);

  // Filter stores based on search and brand
  const filteredStores = useMemo(() => {
    let filtered = stores;

    // Filter by brand
    if (selectedBrand !== 'all') {
      filtered = filtered.filter(store => store.brand === selectedBrand);
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(store => 
        store.name.toLowerCase().includes(search) ||
        store.id.toLowerCase().includes(search) ||
        store.city.toLowerCase().includes(search) ||
        store.state.toLowerCase().includes(search)
      );
    }

    return filtered;
  }, [stores, searchTerm, selectedBrand]);

  // Get unique brands
  const brands = useMemo(() => {
    const uniqueBrands = new Set(stores.map(s => s.brand));
    return Array.from(uniqueBrands).sort();
  }, [stores]);

  const handleChange = (event: SelectChangeEvent<string | string[]>) => {
    const newValue = event.target.value;
    
    if (multiple) {
      // Handle "Select All" for multiple selection
      if (Array.isArray(newValue) && newValue.includes('SELECT_ALL')) {
        if ((value as string[]).length === filteredStores.length) {
          onChange([]);
        } else {
          onChange(filteredStores.map(s => s.id));
        }
        return;
      }
    }
    
    onChange(newValue);
  };

  const handleClearSelection = () => {
    onChange(multiple ? [] : '');
  };

  const getSelectedText = () => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return '';
    }
    
    if (value === 'all') {
      return 'All Stores';
    }
    
    if (Array.isArray(value)) {
      if (value.length === stores.length) {
        return `All ${stores.length} Stores`;
      }
      const selectedStores = stores.filter(s => value.includes(s.id));
      if (value.length <= 3) {
        return selectedStores.map(s => s.name).join(', ');
      }
      return `${value.length} stores selected`;
    }
    
    const selectedStore = stores.find(s => s.id === value);
    return selectedStore ? selectedStore.name : '';
  };

  if (error) {
    return (
      <FormControl fullWidth disabled>
        <InputLabel>{error}</InputLabel>
        <Select value={multiple ? [] : ''} />
      </FormControl>
    );
  }

  return (
    <FormControl fullWidth>
      <InputLabel id="enhanced-location-selector-label">
        {isLoading ? 'Loading stores...' : label}
      </InputLabel>
      <Select
        labelId="enhanced-location-selector-label"
        multiple={multiple}
        value={value}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={() => getSelectedText()}
        MenuProps={MenuProps}
        startAdornment={
          isLoading ? (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          ) : (
            <StoreIcon sx={{ mr: 1, color: 'text.secondary' }} />
          )
        }
        endAdornment={
          (value && (Array.isArray(value) ? value.length > 0 : value !== '')) && (
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClearSelection();
              }}
              sx={{ mr: 1, minWidth: 'auto', p: 0.5 }}
            >
              <ClearIcon fontSize="small" />
            </Button>
          )
        }
      >
        {/* Search Box */}
        {showSearch && (
          <Box sx={{ p: 2, position: 'sticky', top: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search stores..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {/* Brand Filter */}
        {showBrandFilter && brands.length > 1 && (
          <Box sx={{ px: 2, pb: 1, position: 'sticky', top: showSearch ? 64 : 0, backgroundColor: 'background.paper', zIndex: 1 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label="All Brands"
                size="small"
                color={selectedBrand === 'all' ? 'primary' : 'default'}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedBrand('all');
                }}
              />
              {brands.map(brand => (
                <Chip
                  key={brand}
                  label={brand}
                  size="small"
                  color={selectedBrand === brand ? 'primary' : 'default'}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedBrand(brand);
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* All Stores Option */}
        {!multiple && (
          <MenuItem value="all">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon />
              <span>All Stores</span>
              <Chip
                label={`${stores.length} stores`}
                size="small"
                sx={{ ml: 'auto' }}
              />
            </Box>
          </MenuItem>
        )}

        {/* Select All Option for Multiple Selection */}
        {multiple && filteredStores.length > 0 && (
          <MenuItem value="SELECT_ALL">
            <Checkbox
              checked={Array.isArray(value) && value.length === filteredStores.length}
              indeterminate={Array.isArray(value) && value.length > 0 && value.length < filteredStores.length}
            />
            <ListItemText primary={`Select All (${filteredStores.length} stores)`} />
          </MenuItem>
        )}

        {filteredStores.length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No stores found
            </Typography>
          </MenuItem>
        )}

        {/* Group stores by brand */}
        {brands.map(brand => {
          const brandStores = filteredStores.filter(s => s.brand === brand);
          if (brandStores.length === 0) return null;

          return [
            <ListSubheader key={`header-${brand}`}>
              {brand} ({brandStores.length} stores)
            </ListSubheader>,
            ...brandStores.map((store) => (
              <MenuItem key={store.id} value={store.id}>
                {multiple && (
                  <Checkbox checked={Array.isArray(value) && value.includes(store.id)} />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box>{store.name}</Box>
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      #{store.id} • {store.city}, {store.state}
                    </Box>
                  </Box>
                  {store.active ? (
                    <Chip label="Active" size="small" color="success" variant="outlined" sx={{ ml: 2 }} />
                  ) : (
                    <Chip label="Inactive" size="small" variant="outlined" sx={{ ml: 2 }} />
                  )}
                </Box>
              </MenuItem>
            ))
          ];
        })}
      </Select>
      
      {/* Selection Summary */}
      {multiple && Array.isArray(value) && value.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {value.length} store{value.length > 1 ? 's' : ''} selected
          </Typography>
        </Box>
      )}
    </FormControl>
  );
};

export default EnhancedLocationSelector;